"use client"

import { useState } from "react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger
} from "@/components/ui/select"
import type { CasinoProviderDTO } from "@/domain/casino/list-lobby-facets"
import { SearchInput } from "@/features/home/Search"

import {
  defaultSortFilter,
  isSortFilterValue,
  sortFilterValues,
  type SortFilterValue
} from "../data/lobby"
import { lobbyTexts } from "../localization/texts"
import { FilterTriggerLabel } from "./FilterTriggerLabel"
import { ProviderFilter } from "./ProviderFilter"

export type LobbyFiltersProps = {
  providers: CasinoProviderDTO[]
}

type OpenFilterMenu = "providers" | "sort"

export function LobbyFilters({ providers }: LobbyFiltersProps) {
  const [sort, setSort] = useState<SortFilterValue | null>(defaultSortFilter)
  const [openMenu, setOpenMenu] = useState<OpenFilterMenu | null>(null)

  const sortLabel = sort
    ? lobbyTexts.filters.sortOptions[sort]
    : lobbyTexts.filters.sortDefault

  function setMenuOpen(menu: OpenFilterMenu, open: boolean) {
    setOpenMenu((current) => {
      if (open) return menu
      if (current === menu) return null
      return current
    })
  }

  return (
    <section>
      <div className="flex w-full items-center gap-2 md:grid md:grid-cols-[minmax(0,1.9fr)_minmax(0,3.1fr)] md:gap-2 xlg:grid-cols-[minmax(0,3.15fr)_minmax(0,1.85fr)]">
        <div className="hidden min-w-0 md:block">
          <SearchInput
            placeholder={lobbyTexts.searchPlaceholder}
            variant="default"
          />
        </div>

        <div className="flex min-w-0 flex-1 gap-2">
          <div className="min-w-0 flex-1">
            <ProviderFilter
              providers={providers}
              open={openMenu === "providers"}
              onOpenChange={(open) => {
                setMenuOpen("providers", open)
              }}
            />
          </div>

          <div className="min-w-0 flex-1">
            <Select
              open={openMenu === "sort"}
              value={sort ?? ""}
              onOpenChange={(open) => {
                setMenuOpen("sort", open)
              }}
              onValueChange={(value) => {
                if (isSortFilterValue(value)) {
                  setSort(value)
                }
              }}
            >
              <SelectTrigger
                variant="filter"
                aria-label={`${lobbyTexts.filters.sort}: ${sortLabel}`}
              >
                <FilterTriggerLabel
                  prefix={lobbyTexts.filters.sort}
                  value={sortLabel}
                />
              </SelectTrigger>
              <SelectContent
                align="end"
                className="w-[calc(100vw-20px)] border-black-600 bg-black-800 text-white md:w-auto"
              >
                {sortFilterValues.map((value) => (
                  <SelectItem
                    key={value}
                    value={value}
                    className="rounded-[10px] text-white/50 focus:bg-black-800 focus:text-white/50 data-[state=checked]:bg-black-600 data-[state=checked]:text-white data-[state=checked]:focus:bg-black-600 data-[state=checked]:focus:text-white"
                  >
                    {lobbyTexts.filters.sortOptions[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </section>
  )
}
